import Box from '@mui/material/Box';
import CategoryIcon from '@mui/icons-material/Category';
import PersonIcon from '@mui/icons-material/Person';
import SecurityIcon from '@mui/icons-material/Security';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import debounce from 'lodash/debounce';
import { useNavigate } from 'react-router';
import { mindtimeWazuhThreatHuntingApi } from 'src/api/mindtime/wazuhThreatHuntingApi';
import { Table } from 'src/common';
import { getSecurityThreatHuntingDetailHref } from 'src/mindtime/config/securityNav';
import { EDR_SOLUTION_ID } from 'src/mindtime/constants/securitySolutions';
import { useEdrDataGate } from 'src/mindtime/hooks/useEdrDataGate';
import { useSubscriptionModulePermissions } from 'src/mindtime/hooks/useSubscriptionPermissionAccess';
import type {
  WazuhThreatHuntingAgentSummaryData,
  WazuhThreatHuntingFindingItem,
} from 'src/types/mindtime/wazuhThreatHunting';
import type { Column, TableData } from 'src/types/table';
import logger from 'src/utils/logger';
import { getAgentThreatHuntingPreview } from '../../preview/threatHunting';
import {
  buildThreatHuntingAgentFindingsQuery,
  emptyThreatHuntingFacets,
  getThreatHuntingFindingId,
  getThreatHuntingFindingListColumns,
  mapThreatHuntingFacetOptions,
  mergeThreatHuntingFacets,
  THREAT_HUNTING_SEVERITY_FILTER_OPTIONS,
  ThreatSummaryKpiGrid,
  type ThreatHuntingAgentFilters,
  type ThreatHuntingFacets,
} from '../../threat-hunting/threatHuntingDetailModel';
import { useThreatHuntingUserFilterOptions } from '../../threat-hunting/useThreatHuntingUserFilterOptions';
import { EdrDataCard, EdrDataToolbar, type EdrToolbarFilter } from '../../shared/edrPrimitives';

const PAGE_SIZE = 25;

type EndpointThreatHuntingTabProps = {
  systemWazuhAgentId: string;
};

const EndpointThreatHuntingTab: FC<EndpointThreatHuntingTabProps> = ({ systemWazuhAgentId }) => {
  const navigate = useNavigate();
  const { wait, isPreviewMode } = useEdrDataGate('endpoints');
  const { canWidget } = useSubscriptionModulePermissions(EDR_SOLUTION_ID, 'threat-hunting');
  const canSummary = canWidget('summary');
  const canList = canWidget('resultsList');
  const agentId = systemWazuhAgentId.trim();

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(PAGE_SIZE);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ThreatHuntingAgentFilters>({});
  const [facets, setFacets] = useState<ThreatHuntingFacets>(emptyThreatHuntingFacets());
  const [listLoading, setListLoading] = useState(true);
  const [summary, setSummary] = useState<WazuhThreatHuntingAgentSummaryData | null>(null);
  const [items, setItems] = useState<WazuhThreatHuntingFindingItem[]>([]);
  const [totalRecords, setTotalRecords] = useState(0);

  const debouncedSetSearch = useMemo(
    () =>
      debounce((value: string) => {
        setSearch(value);
        setPage(0);
      }, 300),
    []
  );

  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const {
    options: userFilterOptions,
    loading: userFilterLoading,
    onSearch: onUserFilterSearch,
  } = useThreatHuntingUserFilterOptions({
    systemWazuhAgentId: agentId || undefined,
    isPreviewMode,
  });

  const openFindingDetail = useCallback(
    (item: WazuhThreatHuntingFindingItem) => {
      const id = getThreatHuntingFindingId(item);
      if (!id) return;
      navigate(getSecurityThreatHuntingDetailHref(id), { state: { record: item } });
    },
    [navigate]
  );

  useEffect(() => {
    setFacets(emptyThreatHuntingFacets());
  }, [agentId]);

  useEffect(() => {
    if (!agentId) {
      setSummary(null);
      return undefined;
    }
    if (wait) return undefined;
    if (isPreviewMode) {
      setSummary(canSummary ? getAgentThreatHuntingPreview(agentId).summary : null);
      return undefined;
    }

    if (!canSummary) {
      setSummary(null);
      return undefined;
    }

    let cancelled = false;
    mindtimeWazuhThreatHuntingApi
      .getAgentSummary(agentId)
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((error) => {
        if (!cancelled) {
          logger(error, 'error', 'EndpointThreatHuntingTab.summary');
          setSummary(null);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [agentId, wait, isPreviewMode, canSummary]);

  useEffect(() => {
    if (!agentId) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }
    if (wait) {
      setListLoading(true);
      return undefined;
    }
    if (isPreviewMode) {
      if (!canList) {
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
        return undefined;
      }
      const preview = getAgentThreatHuntingPreview(agentId);
      setItems(preview.items);
      setTotalRecords(preview.items.length);
      setFacets(preview.facets);
      setListLoading(false);
      return undefined;
    }

    if (!canList) {
      setItems([]);
      setTotalRecords(0);
      setListLoading(false);
      return undefined;
    }

    let cancelled = false;
    setListLoading(true);
    mindtimeWazuhThreatHuntingApi
      .getFindings(buildThreatHuntingAgentFindingsQuery(agentId, page, rowsPerPage, search, filters))
      .then((data) => {
        if (cancelled) return;
        const nextItems = data?.items ?? [];
        setItems(nextItems);
        setTotalRecords(data?.totalRecords ?? 0);
        setFacets((prev) => mergeThreatHuntingFacets(nextItems, prev));
        setListLoading(false);
      })
      .catch((error) => {
        if (cancelled) return;
        logger(error, 'error', 'EndpointThreatHuntingTab.list');
        setItems([]);
        setTotalRecords(0);
        setListLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [
    agentId,
    page,
    rowsPerPage,
    search,
    filters.user,
    filters.mitreId,
    filters.eventType,
    filters.severityBand,
    wait,
    isPreviewMode,
    canList,
  ]);

  const rows = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        id: getThreatHuntingFindingId(item) || item.detection || '',
      })),
    [items]
  );

  const setFilter = (key: keyof ThreatHuntingAgentFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined }));
    setPage(0);
  };

  const handleReset = () => {
    setFilters({});
    setSearchInput('');
    setSearch('');
    setPage(0);
  };

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'user',
        label: 'User',
        icon: <PersonIcon style={{ fontSize: 14 }} />,
        variant: 'searchable',
        value: filters.user ?? '',
        multiple: true,
        searchableOptions: userFilterOptions,
        searchableLoading: userFilterLoading,
        searchableOnSearch: onUserFilterSearch,
        onChange: (value) => setFilter('user', value),
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: THREAT_HUNTING_SEVERITY_FILTER_OPTIONS,
        value: filters.severityBand ?? '',
        multiple: true,
        onChange: (value) => setFilter('severityBand', value),
      },
      {
        id: 'mitre',
        label: 'MITRE Technique',
        icon: <SecurityIcon style={{ fontSize: 14 }} />,
        options: mapThreatHuntingFacetOptions(facets.mitreIds),
        value: filters.mitreId ?? '',
        multiple: true,
        onChange: (value) => setFilter('mitreId', value),
      },
      {
        id: 'eventType',
        label: 'Event Type',
        icon: <CategoryIcon style={{ fontSize: 14 }} />,
        options: mapThreatHuntingFacetOptions(facets.eventTypes),
        value: filters.eventType ?? '',
        multiple: true,
        onChange: (value) => setFilter('eventType', value),
      },
    ],
    [
      filters.user,
      filters.severityBand,
      filters.mitreId,
      filters.eventType,
      facets.mitreIds,
      facets.eventTypes,
      userFilterOptions,
      userFilterLoading,
      onUserFilterSearch,
    ]
  );

  const columns: Column[] = useMemo(() => getThreatHuntingFindingListColumns('endpoint'), []);

  return (
    <Box display="flex" flexDirection="column" style={{ gap: 16, marginTop: 8, minWidth: 0 }}>
      {canSummary ? <ThreatSummaryKpiGrid summary={summary} /> : null}

      {canList ? (
      <EdrDataCard
        toolbar={(
          <EdrDataToolbar
            searchValue={searchInput}
            onSearchChange={(value) => {
              setSearchInput(value);
              debouncedSetSearch(value);
            }}
            searchPlaceholder="Search results…"
            filters={toolbarFilters}
            onReset={handleReset}
          />
        )}
      >
        <Table
          columns={columns}
          data={rows as unknown as TableData[]}
          loading={listLoading}
          showPagination
          page={page}
          rowsPerPage={rowsPerPage}
          totalRows={totalRecords}
          onPageChange={setPage}
          onRowsPerPageChange={(next) => {
            setRowsPerPage(next);
            setPage(0);
          }}
          onRowClick={(row) => openFindingDetail(row as unknown as WazuhThreatHuntingFindingItem)}
          TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          MUITableProps={{ sx: { tableLayout: 'fixed', width: '100%' } }}
        />
      </EdrDataCard>
      ) : null}
    </Box>
  );
};

export default EndpointThreatHuntingTab;
